import { AppDispatch } from '@/redux'
import { appActions } from '@/redux/slice/appSlice'
import { adminDashboardActions } from '@/redux/slice/adminDashboardSlice'
import { AdminDashboardAPI } from '@/API/AdminDashboard'
import { showNotification } from '@/services/notificationService'
import { toast } from '@/redux/reducers/toast'
import { Toast, ToastVariant } from '@/constants/toast'
import type {
  DashboardSummaryResponse,
  DashboardUserResponse,
  DashboardOrganizationResponse,
  DashboardPaymentResponse,
  DashboardPaymentFailureResponse,
  DashboardCredentialsListResponse,
  DashboardNotificationLogResponse,
  DashboardNotificationLogDetailResponse,
  DashboardMetricsResponse,
  DashboardUsersParams,
  DashboardOrganizationsParams,
  DashboardPaymentsParams,
  DashboardPaymentFailuresParams,
  DashboardCredentialsParams,
  DashboardMetricsParams,
  DashboardNotificationsParams,
  DashboardExportParams,
  AdminMrrResponse,
  AdminTransactionListResponse,
  SyncTransactionsResponse,
  LinkCustomerResponse,
  GetTransactionsParams,
  DashboardSubscriptionsSyncResponse
} from '@/types/adminDashboard'

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export const getDashboardSummary = (token: string) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardSummaryResponse; err?: string }> => {
  dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getSummary(token)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getDashboardUsers = (
  token: string,
  params?: DashboardUsersParams,
  loading = true
) => async (dispatch: AppDispatch): Promise<{ data?: DashboardUserResponse[]; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getUsers(token, params)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const getDashboardOrganizations = (
  token: string,
  params?: DashboardOrganizationsParams,
  loading = true
) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardOrganizationResponse[]; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getOrganizations(token, params)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const getDashboardPayments = (
  token: string,
  params?: DashboardPaymentsParams,
  loading = true
) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardPaymentResponse[]; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getPayments(token, params)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const getDashboardPaymentFailures = (
  token: string,
  params?: DashboardPaymentFailuresParams,
  loading = true
) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardPaymentFailureResponse[]; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getPaymentFailures(token, params)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const syncDashboardSubscriptions = (token: string) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardSubscriptionsSyncResponse; err?: string }> => {
  dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.syncSubscriptions(token)
  dispatch(appActions.setLoading(false))
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
    return resp
  }
  if (resp.data) {
    const { added, updated, platformOnlyCount } = resp.data
    toast({
      title: Toast.SUCCESS,
      variant: ToastVariant.SUCCESS,
      description: `Synced: ${added} added, ${updated} updated, ${platformOnlyCount} platform-only.`
    })
  }
  return resp
}

export const getDashboardMetrics = (
  token: string,
  params?: DashboardMetricsParams,
  loading = true
) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardMetricsResponse; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getMetrics(token, params)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const getDashboardCredentials = (
  token: string,
  params?: DashboardCredentialsParams,
  loading = true
) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardCredentialsListResponse; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getCredentials(token, params)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const getDashboardNotifications = (
  token: string,
  params?: DashboardNotificationsParams,
  loading = true
) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardNotificationLogResponse[]; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getNotifications(token, params)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const getDashboardOrganizationById = (token: string, id: string) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardOrganizationResponse; err?: string }> => {
  dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getOrganizationById(token, id)
  dispatch(appActions.setLoading(false))
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
    return resp
  }
  if (resp.data) {
    dispatch(adminDashboardActions.setOrganizationDetail(resp.data))
  }
  return resp
}

export const getDashboardNotificationById = (token: string, id: string) => async (
  dispatch: AppDispatch
): Promise<{ data?: DashboardNotificationLogDetailResponse; err?: string }> => {
  void dispatch
  const resp = await AdminDashboardAPI.getNotificationById(token, id)
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  return resp
}

export const exportDashboard = (token: string, params: DashboardExportParams) => async (
  dispatch: AppDispatch
): Promise<{ err?: string }> => {
  dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.exportData(token, params)
  dispatch(appActions.setLoading(false))
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
    return { err: resp.err }
  }
  if (params.format === 'csv' && resp.data instanceof Blob && resp.filename) {
    triggerBlobDownload(resp.data, resp.filename)
  }
  return {}
}

export const getAdminMrr = (token: string, loading = true) => async (
  dispatch: AppDispatch
): Promise<{ data?: AdminMrrResponse; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getMrr(token)
  if (resp.data) { 
    dispatch(adminDashboardActions.setMRR(resp.data))
  } else if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const getAdminTransactions = (token: string, params?: GetTransactionsParams, loading = true) => async (
  dispatch: AppDispatch
): Promise<{ data?: AdminTransactionListResponse; err?: string }> => {
  if (loading) dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.getTransactions(token, params)
  if (resp.data) { 
    dispatch(adminDashboardActions.setTransactions(resp.data))
  } else if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
  }
  if (loading) dispatch(appActions.setLoading(false))
  return resp
}

export const syncAdminTransactions = (token: string, params: { startDate: string; endDate: string }) => async (
  dispatch: AppDispatch
): Promise<{ data?: SyncTransactionsResponse; err?: string }> => {
  dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.syncTransactions(token, params)
  dispatch(appActions.setLoading(false))
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
    return resp
  }
  if (resp.data) {
    const { created, updated, alreadySynced, unlinked } = resp.data
    toast({
      title: Toast.SUCCESS,
      variant: ToastVariant.SUCCESS,
      description: `Sync complete: ${created} new, ${updated} updated, ${alreadySynced} already up to date, ${unlinked} unlinked.`
    })
  }
  return resp
}

export const linkAdminStripeCustomer = (
  token: string,
  organizationId: string,
  payload: { stripeCustomerId: string; setPrimary?: boolean }
) => async (dispatch: AppDispatch): Promise<{ data?: LinkCustomerResponse; err?: string }> => {
  dispatch(appActions.setLoading(true))
  const resp = await AdminDashboardAPI.linkStripeCustomer(token, organizationId, payload)
  dispatch(appActions.setLoading(false))
  if (resp.err) {
    showNotification({ data: undefined, err: resp.err }, '', {})
    return resp
  }
  if (resp.data) {
    const { reconciledInvoices, reconciledSubscriptions } = resp.data
    toast({
      title: Toast.SUCCESS,
      variant: ToastVariant.SUCCESS,
      description: `Customer linked. ${reconciledInvoices} invoices and ${reconciledSubscriptions} subscriptions reconciled.`
    })
  }
  return resp
}
